import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { trackModelImageSeen, trackAiImagesOnLoad } from '../tracking/trackingLayer';
import Swiper from 'swiper';
import StickyScroller from '../../scripts/lib/sticky-scroller';
import { mediaQuery } from '../../scripts/lib/mediaQuery';
import { calculateVisiblePx } from './image-visibility';
import imageViewThumbnails from '../image-view-thumbnails/image-view-thumbnails.vue.component';

export default {
  template: document.getElementById('image-main-image-view-template').innerHTML,

  components: {
    'image-view-thumbnails': imageViewThumbnails,
  },

  props: {
    productId: {
      type: Number,
      required: true,
    },
    images: {
      type: Array,
      required: true,
    },
    previewImage: {
      type: Object,
      required: false,
      default: null,
    },
    active: {
      type: Object,
      required: true,
    },
    brandName: {
      type: String,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    modelMeasurementsInfo: {
      type: Boolean,
      required: false,
    },
    hasAiImageMixture: {
      type: Boolean,
      required: false,
      default: false,
    },
    animate: {
      type: Boolean,
      required: false,
      default: false,
    },
    inAnimationTest: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const swiper = ref(null);
    const swiperInstance = ref(null);
    const swiperIndex = ref(0);
    const activeThumbnail = ref(null);
    const imageTrackingSent = ref({});
    const observer = ref(null);
    const modelViewTrackingSent = ref(false);
    const parallaxHandler = ref(null);
    const parallaxRaf = ref(null);
    const parallaxRoot = ref(null);
    const parallaxOverlays = ref(null);
    const parallaxSection = ref(null);
    const stickyScroller = ref(null);
    const lineIndicators = ref(null);
    const pageLoaded = ref({});
    const pageResized = ref(false);

    const showPreviewImage = computed(
      () =>
        props.previewImage &&
        props.images[0]?.fallbackStandardRedesignUrl !== props.previewImage.fallbackStandardRedesignUrl,
    );

    const imageAltTag = computed(() => {
      return `${props.brandName} ${props.productName}${props.active.colors?.active?.name ? ': ' + props.active.colors.active.name : ''}`.trim();
    });

    function shouldTrackModelImageSeen(index) {
      return index > 0 && (props.hasAiImageMixture || props.inAnimationTest);
    }

    function buildSrcSet(view) {
      return (
        [
          [view.thumbnailUrl, '144w'],
          [view.thumbnailRetinaUrl, '288w'],
          [view.standardUrl, '435w'],
          [view.standardRedesignUrl, '680w'],
          [view.standardRetinaUrl, '870w'],
          [view.zoomUrl, '1122w'],
          [view.standardRedesignRetinaUrl, '1360w'],
          [view.zoomRetinaUrl, '2244w'],
        ]
          // remove non existent urls
          .filter(([src]) => !!src)
          // build srcset string
          .map(([src, size]) => `${src} ${size}`)
          .join(',\n')
      );
    }

    function openZoomOverlay(index) {
      document.dispatchEvent(new CustomEvent('ents:zoom-layer:open', { detail: { index } }));
    }

    function setPageLoaded() {
      pageLoaded.value = true;
    }

    function initializeSwiperSlider() {
      if (swiperInstance.value) {
        swiperInstance.value.destroy();
        swiperInstance.value = null;
      }

      const swiperConfig = {
        modules: [],
        enabled: props.images.length > 1,
        initialSlide: swiperIndex.value,
        autoplay: false,
        loop: true,
        spaceBetween: 10,
        breakpoints: {
          560: {
            slidesPerView: 1.6,
            centeredSlides: true,
            // This can be changed back to true once swiper #7239 has been resolved
            loop: props.images.length > 3,
          },
          // 710 and 860 are not breakpoints we usually use in our library.
          // When the slider only gets wider but not higher anymore, it would make the images float away from each other.
          // So we need the breakpoints in this case to show "more" of the cut-off images and preserve the spacings between them.
          710: {
            slidesPerView: 2,
            centeredSlides: true,
            // This can be changed back to true once swiper #7239 has been resolved
            loop: props.images.length > 3,
          },
          860: {
            slidesPerView: 2.2,
            centeredSlides: true,
            // This can be changed back to true once swiper #7239 has been resolved
            loop: props.images.length > 3,
          },
          960: {
            loop: false,
          },
        },
        on: {
          slideChange: () => {
            if (swiperInstance.value && !swiperInstance.value.destroyed) {
              if (Number.isNaN(swiperInstance.value.realIndex)) {
                swiperIndex.value = 0;
              } else {
                swiperIndex.value = swiperInstance.value.realIndex;
              }
            }
          },
          beforeResize: () => {
            // reinit swiper on page resize so all slides are properly shown
            // solves bug with disappearing slide to the left of the active slide
            //
            // 'pageResized' flag is necessary because Swiper's resize handler
            // is always triggered once after initialization, but we only want
            // to reinit Swiper on actual page resizes
            if (pageResized.value) {
              initializeSwiperSlider();
              pageResized.value = false;
            } else {
              pageResized.value = true;
            }
          },
        },
      };

      swiperInstance.value = new Swiper(swiper.value, swiperConfig);
    }

    function imageObserver() {
      if (observer.value instanceof IntersectionObserver) {
        observer.value.disconnect();
        imageTrackingSent.value = {};
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      };

      const desktopImages = document.querySelectorAll('.ents-image-view__desktop .ents-image-view__entry');
      const mobileImages = document.querySelectorAll('.ents-image-view__mobile .swiper-slide');

      observer.value = new IntersectionObserver(() => {
        compareDesktopImageVisibility(desktopImages);
      }, options);

      Array.from(desktopImages)
        .concat(Array.from(mobileImages))
        .forEach((el) => {
          const imageIndex =
            el.getAttribute('data-ents-image-view-index') || el.getAttribute('data-swiper-slide-index');
          imageTrackingSent.value[imageIndex] = false;
          observer.value.observe(el);
        });
    }

    function compareDesktopImageVisibility(els) {
      let max = 0;

      for (const el of Array.from(els)) {
        if (el.offsetHeight === 0) {
          // important, otherwise visiblePercentage will be "infinity", which is > 40%
          return;
        }
        const elementIndex = el.getAttribute('data-ents-image-view-index');

        // calculate number of visible pixels (vertically, i.e. rows)
        const visiblePx = calculateVisiblePx(true, el);

        if (visiblePx > max) {
          max = visiblePx;
          activeThumbnail.value = parseInt(elementIndex, 10);
        }
      }
    }

    function teardownParallax() {
      if (parallaxRaf.value !== null) {
        cancelAnimationFrame(parallaxRaf.value);
        parallaxRaf.value = null;
      }
      if (parallaxHandler.value) {
        window.removeEventListener('scroll', parallaxHandler.value);
        parallaxHandler.value = null;
      }
      if (parallaxOverlays.value) {
        parallaxOverlays.value.forEach((overlay) => {
          overlay.style.removeProperty('transform');
          overlay.style.removeProperty('will-change');
        });
        parallaxOverlays.value = null;
      }
      if (parallaxSection.value) {
        parallaxSection.value.style.removeProperty('--parallax-zoom');
        parallaxSection.value = null;
      }
      if (parallaxRoot.value) {
        parallaxRoot.value.classList.remove('parallax-css');
        parallaxRoot.value.style.removeProperty('--parallax-max-shift');
        parallaxRoot.value.style.removeProperty('--parallax-scroll-start');
        parallaxRoot.value.style.removeProperty('--parallax-scroll-end');
        parallaxRoot.value = null;
      }
    }

    // Mobile parallax (summary overlays shift up over the pinned, zooming hero) runs two ways:
    // non-touch on the compositor via CSS scroll-driven animations; touch from JS, because CSS
    // scroll-driven flickers on iOS during finger-down dragging.
    function initializeParallax() {
      teardownParallax(); // Re-runs on viewport width changes, so always tear down first

      if (!props.animate || mediaQuery.L_and_up || mediaQuery.prefersReducedMotion()) {
        return;
      }

      const mobileSection = document.querySelector('.ents-image-view__mobile--parallax');
      const overlays = Array.from(document.querySelectorAll('.ents-pds-summary--animated'));
      const parallaxScope = document.querySelector('.ents-image-views-container--animated');

      if (!mobileSection || !overlays.length || !parallaxScope) {
        return;
      }

      parallaxSection.value = mobileSection;
      parallaxOverlays.value = overlays;
      parallaxRoot.value = parallaxScope;

      const initialOverlayTop = overlays[0].getBoundingClientRect().top + window.scrollY;
      const parallaxFactor = 0.8;
      const maxZoom = 1.2;

      const imageColumn = parallaxScope.querySelector('.ents-image-views') ?? mobileSection;
      const columnRect = imageColumn.getBoundingClientRect();
      const columnTop = columnRect.top + window.scrollY;
      const columnBottom = columnRect.bottom + window.scrollY;
      const imageRevealMargin = columnTop / 2;
      const parallaxStart = Math.max(0, columnBottom - window.innerHeight + imageRevealMargin);

      const overlayTravel = Math.max(0, initialOverlayTop - parallaxStart);
      const maxShift = (overlayTravel * parallaxFactor) / (1 + parallaxFactor);

      // Keep the document height constant against the overlays' upward shift (CSS turns this
      // into a negative margin on the last overlay) so scroll-anchoring doesn't fight it.
      parallaxScope.style.setProperty('--parallax-max-shift', `${maxShift}px`);
      parallaxScope.style.setProperty('--parallax-scroll-start', `${parallaxStart}px`);

      if (!mediaQuery.checkIfTouchDevice() && CSS.supports('animation-timeline: scroll()')) {
        parallaxScope.style.setProperty('--parallax-scroll-end', `${parallaxStart + maxShift / parallaxFactor}px`);
        parallaxScope.classList.add('parallax-css');
        return;
      }

      // Promote the overlays so each per-frame translate composites instead of repainting the
      // full-bleed summary subtree.
      overlays.forEach((overlay) => {
        overlay.style.willChange = 'transform';
      });

      let lastShift = null;
      const applyParallax = () => {
        parallaxRaf.value = null;
        const scrolledPastStart = Math.max(0, window.scrollY - parallaxStart);
        const shift = Math.min(scrolledPastStart * parallaxFactor, maxShift);
        if (shift === lastShift) {
          return;
        }
        lastShift = shift;
        overlays.forEach((overlay) => {
          overlay.style.transform = `translateY(${-shift}px)`;
        });
        const progress = maxShift > 0 ? shift / maxShift : 0;
        mobileSection.style.setProperty('--parallax-zoom', 1 + progress * (maxZoom - 1));
      };

      parallaxHandler.value = () => {
        if (parallaxRaf.value === null) {
          parallaxRaf.value = requestAnimationFrame(applyParallax);
        }
      };
      window.addEventListener('scroll', parallaxHandler.value, { passive: true });
      applyParallax(); // apply for the current scroll position right away
    }

    // Derive the bar from stable geometry (swiper centre + any slide's equal-width image box),
    // not the active slide's live position (transient mid-swipe). X only; Y is set in CSS.
    function positionLineIndicators() {
      if (!swiper.value || !lineIndicators.value) {
        return;
      }

      const imageBox = swiper.value.querySelector('.ents-image-view__mobile--spacer');
      const overlay = lineIndicators.value.offsetParent;

      if (!imageBox || !overlay) {
        return;
      }

      const imageRect = imageBox.getBoundingClientRect();

      if (imageRect.width === 0) {
        return; // hidden (e.g. desktop breakpoint)
      }

      const swiperRect = swiper.value.getBoundingClientRect();
      const overlayRect = overlay.getBoundingClientRect();
      const left = swiperRect.left + swiperRect.width / 2 - imageRect.width / 2 - overlayRect.left;
      lineIndicators.value.style.left = `${left}px`;
      lineIndicators.value.style.width = `${imageRect.width}px`;
    }

    // Desktop only — on mobile StickyScroller forces a sync layout every scroll frame.
    function updateStickyScroller() {
      const desktop = mediaQuery.L_and_up;
      if (desktop && !stickyScroller.value) {
        const wrapper = document.querySelector('.ents-pds-summary__wrapper--sticky');
        stickyScroller.value = new StickyScroller(wrapper);
      }
      if (!desktop && stickyScroller.value) {
        stickyScroller.value.destroy();
        stickyScroller.value = null;
      }
    }

    function onWidthChange() {
      initializeParallax();
      updateStickyScroller();
    }

    function onWindowLoad() {
      setPageLoaded();
      positionLineIndicators();
      initializeParallax();
    }

    watch(activeThumbnail, (index) => {
      if (!modelViewTrackingSent.value && shouldTrackModelImageSeen(index)) {
        trackModelImageSeen();
        modelViewTrackingSent.value = true;
      }
    });

    watch(swiperIndex, (index) => {
      if (!modelViewTrackingSent.value && shouldTrackModelImageSeen(index)) {
        trackModelImageSeen();
        modelViewTrackingSent.value = true;
      }
    });

    onMounted(() => {
      initializeSwiperSlider();
      imageObserver();

      if (props.hasAiImageMixture) {
        trackAiImagesOnLoad();
      }

      initializeParallax();

      nextTick(positionLineIndicators);
      // Indicators track the centered image pixel-accurately (its width scales
      // with the viewport within a breakpoint), so reposition on every resize.
      window.addEventListener('resize', positionLineIndicators, { passive: true });
      // Re-init on width changes so maxShift tracks the hero-image height.
      mediaQuery.on('change:width', onWidthChange);
      updateStickyScroller();

      if (document.readyState === 'complete') {
        onWindowLoad();
      } else {
        window.addEventListener('load', onWindowLoad, { once: true });
      }
    });

    onUnmounted(() => {
      teardownParallax();
      window.removeEventListener('resize', positionLineIndicators);
      window.removeEventListener('load', onWindowLoad);
      mediaQuery.off('change:width', onWidthChange);
      if (stickyScroller.value) {
        stickyScroller.value.destroy();
        stickyScroller.value = null;
      }
    });

    watch(
      () => props.images,
      () => {
        nextTick(() => {
          initializeSwiperSlider();
          imageObserver();
          positionLineIndicators();
        });
      },
    );

    return {
      showPreviewImage,
      swiperIndex,
      activeThumbnail,
      buildSrcSet,
      openZoomOverlay,
      swiper,
      lineIndicators,
      imageAltTag,
    };
  },
};
